import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from '../cn.ts';
import { Spinner } from './Spinner.tsx';

export type ButtonVariant = 'solid' | 'faded' | 'outline' | 'ghost' | 'link';
/** Same palette names as the dashboard's Button; `blue` is the brand colour (w3send teal-green). */
export type ButtonColor = 'blue' | 'ruby' | 'amber' | 'slate' | 'teal';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

// Class strings lifted from the fork's components-next/button/Button.vue.
const COLORS: Record<ButtonColor, Record<ButtonVariant, string>> = {
  blue: {
    solid:
      'bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent',
    faded:
      'bg-n-brand/10 text-n-blue-11 hover:enabled:bg-n-brand/20 focus-visible:bg-n-brand/20 outline-transparent',
    outline: 'text-n-blue-11 outline-n-brand',
    ghost: 'text-n-blue-11 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent',
    link: 'text-n-blue-11 hover:enabled:underline focus-visible:underline outline-transparent',
  },
  ruby: {
    solid: 'bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent',
    faded:
      'bg-n-ruby-9/10 text-n-ruby-11 hover:enabled:bg-n-ruby-9/20 focus-visible:bg-n-ruby-9/20 outline-transparent',
    outline: 'text-n-ruby-11 hover:enabled:bg-n-ruby-9/10 focus-visible:bg-n-ruby-9/10 outline-n-ruby-8',
    ghost: 'text-n-ruby-11 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent',
    link: 'text-n-ruby-9 dark:text-n-ruby-11 hover:enabled:underline focus-visible:underline outline-transparent',
  },
  amber: {
    solid:
      'bg-n-amber-9 text-n-amber-12 dark:text-n-amber-3 hover:enabled:bg-n-amber-10 focus-visible:bg-n-amber-10 outline-transparent',
    faded:
      'bg-n-amber-9/10 text-n-slate-12 hover:enabled:bg-n-amber-9/20 focus-visible:bg-n-amber-9/20 outline-transparent',
    outline: 'text-n-amber-11 hover:enabled:bg-n-amber-9/10 focus-visible:bg-n-amber-9/10 outline-n-amber-9',
    ghost: 'text-n-amber-9 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent',
    link: 'text-n-amber-9 hover:enabled:underline focus-visible:underline outline-transparent',
  },
  slate: {
    solid:
      'bg-n-button-color text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 dark:hover:enabled:bg-n-solid-2 dark:focus-visible:bg-n-solid-2 outline-n-container',
    faded:
      'bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent',
    outline: 'text-n-slate-11 outline-n-strong hover:enabled:bg-n-slate-9/10 focus-visible:bg-n-slate-9/10',
    ghost: 'text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent',
    link: 'text-n-slate-11 hover:enabled:text-n-slate-12 hover:enabled:underline focus-visible:underline outline-transparent',
  },
  teal: {
    solid: 'bg-n-teal-9 text-white hover:enabled:bg-n-teal-10 focus-visible:bg-n-teal-10 outline-transparent',
    faded:
      'bg-n-teal-9/10 text-n-teal-11 hover:enabled:bg-n-teal-9/20 focus-visible:bg-n-teal-9/20 outline-transparent',
    outline: 'text-n-teal-11 hover:enabled:bg-n-teal-9/10 focus-visible:bg-n-teal-9/10 outline-n-teal-9',
    ghost: 'text-n-teal-9 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent',
    link: 'text-n-teal-9 hover:enabled:underline focus-visible:underline outline-transparent',
  },
};

const SIZES: Record<ButtonSize, { regular: string; iconOnly: string; text: string; press: string }> = {
  xs: { regular: 'h-6 px-2', iconOnly: 'h-6 w-6 p-0', text: 'text-xs', press: 'active:enabled:scale-[0.97]' },
  sm: { regular: 'h-8 px-3', iconOnly: 'h-8 w-8 p-0', text: 'text-sm', press: 'active:enabled:scale-[0.97]' },
  md: {
    regular: 'h-10 px-4',
    iconOnly: 'h-10 w-10 p-0',
    text: 'text-sm font-medium',
    press: 'active:enabled:scale-[0.98]',
  },
  lg: {
    regular: 'h-12 px-5',
    iconOnly: 'h-12 w-12 p-0',
    text: 'text-base',
    press: 'active:enabled:scale-[0.98]',
  },
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  /** Leading icon (e.g. a lucide-react icon). */
  icon?: ReactNode;
  /** Put the icon after the label. */
  trailingIcon?: boolean;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  justify?: 'start' | 'center' | 'end';
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'solid',
  color = 'blue',
  size = 'md',
  icon,
  trailingIcon = false,
  loading = false,
  justify = 'center',
  type = 'button',
  disabled,
  className,
  children,
  ref,
  ...rest
}: ButtonProps) {
  const iconOnly = children === undefined || children === null || children === false;
  const sizing = SIZES[size];
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline outline-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer select-none',
        COLORS[color][variant],
        variant === 'link'
          ? 'p-0 font-medium underline-offset-2'
          : iconOnly
            ? sizing.iconOnly
            : sizing.regular,
        sizing.text,
        sizing.press,
        { start: 'justify-start', center: 'justify-center', end: 'justify-end' }[justify],
        trailingIcon && !iconOnly && 'flex-row-reverse',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : icon}
      {!iconOnly && <span className="min-w-0 truncate">{children}</span>}
    </button>
  );
}
